// ============================================================
// Core Game Types - Runes of Eternal Auto-Battler v2.0
// ============================================================
/** Display names for hero classes */
export const HERO_CLASS_NAMES = {
    warrior: 'Warrior',
    tank: 'Tank',
    wizard: 'Wizard',
    archer: 'Ranger',
    healer: 'Healer',
    assassin: 'Assassin',
    druid: 'Druid',
    shaman: 'Shaman',
};
/** Unique RPG character names per class (used as hero.name at creation) */
export const HERO_CHARACTER_NAMES = {
    warrior: 'Goomar',
    tank: 'Ironheld',
    wizard: 'Zyphara',
    archer: 'Sylvari',
    healer: 'Lumiel',
    assassin: 'Kael',
    druid: 'Thorne',
    shaman: 'Zhorak',
};
export const CLASS_RESOURCE = {
    warrior: 'rage',
    tank: 'mana',
    wizard: 'mana',
    archer: 'mana',
    healer: 'mana',
    assassin: 'energy',
    druid: 'mana',
    shaman: 'mana',
};
/** Color for resource bars */
export const RESOURCE_COLORS = {
    mana: '#3b82f6',
    rage: '#ef4444',
    energy: '#eab308',
};
/** Which archetype owns each sub-type — used by ARMOR_CLASS_RESTRICTIONS lookup. */
export const ARMOR_SUBTYPE_ARCHETYPE = {
    plate: 'heavy', brigandine: 'heavy',
    skirmisher: 'medium', hunter: 'medium',
    robe: 'light', vestment: 'light',
};
/** Which classes can wear which armor types */
export const ARMOR_CLASS_RESTRICTIONS = {
    heavy: ['warrior', 'tank'],
    medium: ['warrior', 'archer', 'druid', 'shaman'],
    light: ['wizard', 'healer', 'archer', 'assassin', 'druid', 'shaman'],
};
/** Weapon types that occupy both slots (2H) — cannot equip anything in weapon2 */
export const TWO_HANDED_WEAPON_TYPES = ['staff', 'spear', 'bow', 'sword_2h', 'mace_2h'];
/** Which classes can equip which weapon types */
export const WEAPON_CLASS_RESTRICTIONS = {
    sword: ['warrior', 'assassin', 'tank', 'wizard'],
    axe: ['warrior', 'shaman'],
    mace: ['warrior', 'tank', 'assassin', 'druid', 'shaman'],
    dagger: ['assassin'],
    staff: ['wizard', 'healer', 'druid', 'shaman'],
    bow: ['archer'],
    shield: ['tank', 'warrior'],
    shuriken: ['assassin'],
    spear: ['tank', 'archer'], // warrior removed — gets 2H sword/mace instead
    focus: ['wizard', 'healer', 'druid', 'shaman'],
    throwing_knife: ['assassin'],
    wand: ['wizard', 'healer', 'druid', 'shaman'],
    sword_2h: ['warrior', 'tank'],
    mace_2h: ['warrior', 'tank', 'druid'],
};
export const CROSSROADS_MODIFIERS = {
    // Numbers rebalanced for the 5-zone window. Old values were tuned for a
    // single zone; multiplied uptime ×5 so each effect is roughly halved (net
    // value still ~2.5× the old single-zone payout, but the choice now feels
    // like a real commitment over a stretch of stages).
    //
    // BASE values below are the stage-5 reference. Actual in-game values are
    // scaled by stage via `scaleCrossroadsModifier` — the deltas grow as the
    // player climbs, so a Riches pick at stage 50 is genuinely scarier than
    // the same pick at stage 5. See `getCrossroadsScale` for the curve.
    // Riches — modest gold bump now, much heavier ATK penalty per user
    // balance pass (was +25% gold / +20% atk; now +15% gold / +45% atk).
    riches: {
        id: 'riches', name: 'Riches', accent: 0xa07820,
        description: '+15% gold · enemies hit 45% harder',
        lootMult: 1.15,
        enemyAtkMult: 1.45,
    },
    // Iron Path — modest HP buff, paid for by enemies that close the gap faster.
    iron_path: {
        id: 'iron_path', name: 'Iron Path', accent: 0x9ca3af,
        description: '+12% max HP · enemies move 50% faster',
        hpMult: 1.12,
        enemySpeedMult: 1.50,
    },
    // Pilgrim's Path — XP bump unchanged, enemy-count cost bumped 100% → 120%.
    pilgrims_path: {
        id: 'pilgrims_path', name: "Pilgrim's Path", accent: 0x86efac,
        description: '+25% XP · 120% more enemies per wave',
        xpMult: 1.25,
        enemyCountMult: 2.2,
    },
    // Cursed Riches — loot trimmed 30% → 15% per user balance pass (the
    // -40% HP cost was already brutal; the +30% loot wasn't justifying it
    // in playtest, so the loot is dialed back to keep the pick readable as
    // "extreme risk" rather than "the obvious meta pick").
    cursed_riches: {
        id: 'cursed_riches', name: 'Cursed Riches', accent: 0xef4444,
        description: '+15% loot · −40% max HP',
        lootMult: 1.15,
        hpMult: 0.60,
    },
    // Hermit's Vow — pure XP focus. The scholar walks past the treasure to
    // study the foe; XP per kill jumps but the loot/gold trickle drops. Slots
    // into builds where the player wants to push hero levels (and unlock
    // talent ranks) ahead of gear.
    hermits_vow: {
        id: 'hermits_vow', name: "Hermit's Vow", accent: 0x8a72c8,
        description: '+50% XP · −25% loot',
        xpMult: 1.50,
        lootMult: 0.75,
    },
    // Glacial Path — tempo-control. Enemies move slower (a true buff to
    // anyone who relies on positioning / kiting / charge prep), but XP gains
    // shrink because slow fights spread the same XP over more wave time.
    // Distinct from the other paths because BOTH "knobs" are favourable in
    // the strict sense (slower foes + reduced XP only hurts levelling, not
    // survival), which makes it the "safety pick" of the rotation.
    glacial_path: {
        id: 'glacial_path', name: 'Glacial Path', accent: 0x6090c8,
        description: '−40% enemy speed · −30% XP',
        enemySpeedMult: 0.60,
        xpMult: 0.70,
    },
};
/** Stage-based scaling factor for Crossroads modifier deltas. Linear ramp:
 *  stage ≤5 = 1.0× (base values), then +0.06 per stage. So at stage 25 the
 *  deltas are 2.2×, at stage 50 they're 3.7×. Buffs and debuffs both grow,
 *  so the choice gets genuinely scary in late game (a Riches pick at 50
 *  triples enemy attack vs. the gentle stage-5 version) — and the upside
 *  scales with it so the offer still tempts. */
export function getCrossroadsScale(stage) {
    return 1 + 0.06 * Math.max(0, stage - 5);
}
/** Per-field caps so multiplied effects don't render the run unwinnable
 *  (or trivial) at very high stages. `buffCap` is the max delta above 1.0;
 *  `debuffFloor` is the lower bound on the multiplier itself. Different
 *  fields have asymmetric tuning because their gameplay impact differs:
 *  enemy ATK can scale up to ×5.0 because hits are tempered by hero defense,
 *  but HP debuffs floor at 10% so the run stays winnable, and enemy speed
 *  has a 0.20 floor so foes always eventually reach the heroes. */
const FIELD_BOUNDS = {
    lootMult: { buffCap: 2.0, debuffFloor: 0.10 },
    xpMult: { buffCap: 2.0, debuffFloor: 0.10 },
    hpMult: { buffCap: 1.5, debuffFloor: 0.10 },
    enemyAtkMult: { buffCap: 4.0, debuffFloor: 0.10 },
    enemySpeedMult: { buffCap: 2.0, debuffFloor: 0.20 },
    enemyCountMult: { buffCap: 4.0, debuffFloor: 0.20 },
};
function scaleField(base, scale, field) {
    if (base === undefined || base === 1)
        return base;
    const bounds = FIELD_BOUNDS[field];
    if (base > 1) {
        const delta = Math.min((base - 1) * scale, bounds.buffCap);
        return 1 + delta;
    }
    // base < 1
    const negDelta = (1 - base) * scale;
    const result = 1 - negDelta;
    return Math.max(bounds.debuffFloor, result);
}
/** Returns a copy of the modifier with all multipliers scaled by stage,
 *  plus a re-rendered `description` showing the actual numbers the player
 *  is committing to. Caller should pass the stage at which the modifier
 *  was/will be applied (i.e. `crossroadsModAppliedAtStage`). */
export function scaleCrossroadsModifier(mod, stage) {
    const s = getCrossroadsScale(stage);
    if (s === 1)
        return mod;
    const lootMult = scaleField(mod.lootMult, s, 'lootMult');
    const xpMult = scaleField(mod.xpMult, s, 'xpMult');
    const hpMult = scaleField(mod.hpMult, s, 'hpMult');
    const enemyAtkMult = scaleField(mod.enemyAtkMult, s, 'enemyAtkMult');
    const enemySpeedMult = scaleField(mod.enemySpeedMult, s, 'enemySpeedMult');
    const enemyCountMult = scaleField(mod.enemyCountMult, s, 'enemyCountMult');
    // Re-render the description using the scaled values so the picker shows
    // the player exactly what they're committing to at this stage.
    const pct = (m) => {
        if (m === undefined || m === 1)
            return '';
        const delta = Math.round((m - 1) * 100);
        return `${delta >= 0 ? '+' : ''}${delta}%`;
    };
    const negPct = (m) => {
        if (m === undefined || m === 1)
            return '';
        const delta = Math.round((1 - m) * 100);
        return `−${delta}%`;
    };
    const parts = [];
    if (mod.id === 'riches') {
        parts.push(`${pct(lootMult)} gold`);
        parts.push(`enemies hit ${pct(enemyAtkMult).replace('+', '')} harder`);
    }
    else if (mod.id === 'iron_path') {
        parts.push(`${pct(hpMult)} max HP`);
        parts.push(`enemies move ${pct(enemySpeedMult).replace('+', '')} faster`);
    }
    else if (mod.id === 'pilgrims_path') {
        parts.push(`${pct(xpMult)} XP`);
        parts.push(`${pct(enemyCountMult).replace('+', '')} more enemies per wave`);
    }
    else if (mod.id === 'cursed_riches') {
        parts.push(`${pct(lootMult)} loot`);
        parts.push(`${negPct(hpMult)} max HP`);
    }
    else if (mod.id === 'hermits_vow') {
        parts.push(`${pct(xpMult)} XP`);
        parts.push(`${negPct(lootMult)} loot`);
    }
    else if (mod.id === 'glacial_path') {
        parts.push(`enemies move ${negPct(enemySpeedMult)} slower`);
        parts.push(`${negPct(xpMult)} XP`);
    }
    const description = parts.join(' · ');
    return { ...mod, lootMult, xpMult, hpMult, enemyAtkMult, enemySpeedMult, enemyCountMult, description };
}
/** XP sharing multiplier by deployed hero count */
export const XP_SHARE_MULT = {
    1: 1.0,
    2: 0.8,
    3: 0.65,
    4: 0.55,
    5: 0.45,
    6: 0.40,
};
/** Gold cost to revive a hero after free respawn is used */
export const GOLD_REVIVE_COST_PER_LEVEL = 40;
//# sourceMappingURL=game.js.map